import { JSX, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    useGetProductDetailsV2Query,
    useGetProductDownloadsQuery,
} from "../../common/api/productDetails";
import { ProductDetailsBody } from "../../common/components/productDetail/ProductDetailsBody";
import { ProductDetailsCarousel } from "../../common/components/productDetail/ProductDetailsCarousel";
import { useProductAssociatedProducts } from "../../common/hooks/productDetail/useProductAssociatedProducts";
import { useProductDetailGallery } from "../../common/hooks/productDetail/useProductDetailGallery";
import { useProductDetailSession } from "../../common/hooks/productDetail/useProductDetailSession";
import { useRedirectOnProductDetailNotFound } from "../../common/hooks/productDetail/useRedirectOnProductDetailNotFound";
import { isProductDetailPageLoading } from "../../common/utils/productDetail/isProductDetailPageLoading";
import { showProductCarouselSection } from "../../common/utils/productDetail/productDetailSectionVisibility";
import { ProductDetailsHeader } from "./components/ProductDetailsHeader";
import { ProductDetailsInfoBar } from "./components/ProductDetailsInfoBar";

const ProductDetailsPageContainer = (): JSX.Element | null => {
    const { productId } = useParams<{ productId: string }>();
    const [isDownloading, setIsDownloading] = useState(false);

    const {
        translationsLoaded,
        activeLanguage,
        communicationEmail,
        isUserPending,
        activeCountryId,
    } = useProductDetailSession();

    const shouldFetch = !!productId && !!activeLanguage && !!activeCountryId;

    const { data, isLoading, isFetching, isError, error } =
        useGetProductDetailsV2Query(
            { productId: productId ?? "", countryId: activeCountryId ?? 0 },
            { skip: !shouldFetch },
        );

    const isProductNotFound = useRedirectOnProductDetailNotFound({
        shouldFetch,
        isLoading,
        isFetching,
        isError,
        error,
    });

    const { data: downloads, isLoading: isDownloadsLoading } =
        useGetProductDownloadsQuery(
            { productId: productId ?? "", countryId: activeCountryId ?? 0 },
            { skip: !shouldFetch },
        );

    const isProductPending = !shouldFetch || isLoading || isFetching;
    const isPageLoading = useMemo(
        () =>
            isProductDetailPageLoading({
                isUserLoading: isUserPending,
                translationsLoaded,
                activeLanguage,
                isProductPending,
            }),
        [isUserPending, translationsLoaded, activeLanguage, isProductPending],
    );

    const {
        accessories,
        similarProducts,
        addOns,
        olderModels,
        isAssociationsLoading,
    } = useProductAssociatedProducts(productId, activeCountryId, isPageLoading);

    const openGallery = useProductDetailGallery();

    if (isProductNotFound) {
        return null;
    }

    return (
        <div className="p-product-details">
            <ProductDetailsHeader
                data={data}
                isPageLoading={isPageLoading}
                activeLanguage={activeLanguage}
            />

            {showProductCarouselSection(
                isPageLoading,
                data?.productImages?.length ?? 0,
            ) && (
                <ProductDetailsCarousel
                    isLoading={isPageLoading}
                    images={data?.productImages ?? []}
                    onImageClick={openGallery}
                />
            )}

            <ProductDetailsInfoBar
                data={data}
                isPageLoading={isPageLoading}
                communicationEmail={communicationEmail}
                isDownloading={isDownloading}
                onDownloadStart={() => setIsDownloading(true)}
                onDownloadEnd={() => setIsDownloading(false)}
            />

            <ProductDetailsBody
                data={data}
                isPageLoading={isPageLoading}
                translationsLoaded={translationsLoaded}
                activeLanguage={activeLanguage}
                accessories={accessories}
                similarProducts={similarProducts}
                addOns={addOns}
                olderModels={olderModels}
                isAssociationsLoading={isAssociationsLoading}
                marketingDownloads={downloads?.marketing ?? {}}
                technicalDownloads={downloads?.technical ?? {}}
                marketingDownloadsCount={downloads?.marketingCount ?? 0}
                technicalDownloadsCount={downloads?.technicalCount ?? 0}
                downloadsLoading={isDownloadsLoading}
            />
        </div>
    );
};

export default ProductDetailsPageContainer;
